import { useEffect, useRef, useState } from 'react';
import type { ComponentProps } from 'react';
import { Input } from '@immo/ui-kit';

/*
 * A filter input whose INTERMEDIATE values are meaningless — a `datetime-local`
 * with only two segments filled, a half-typed UUID. It holds a draft and calls
 * `onCommit` on blur or Enter only, so the caller's commit (a URL write, and
 * with it a router navigation and a loader re-run) fires once per interaction
 * instead of once per keystroke.
 *
 * The draft/resync logic is FilterBar's, minus the timer — there is nothing to
 * debounce in a field with no partial value, only something to defer to the end
 * of the interaction. Lifted rather than re-derived on purpose (BL-033): the
 * commit semantics live where the input is, or the next filter added to this app
 * hand-rolls a fourth variant.
 *
 * Why the draft is not optional (BL-036): a `datetime-local` reads as `''` until
 * every segment is filled, so a page that wired `onChange` straight to the URL
 * deleted the very parameter its own `value` was read from, and the browser
 * cleared the control mid-edit.
 */

type CommitOnBlurInputProps = {
  readonly value: string;
  readonly onCommit: (next: string) => void;
} & Omit<ComponentProps<typeof Input>, 'value' | 'onChange' | 'onBlur' | 'onKeyDown'>;

export function CommitOnBlurInput({ value, onCommit, ...inputProps }: CommitOnBlurInputProps) {
  const [draft, setDraft] = useState(value);
  const lastCommitted = useRef(value);

  // Follow the prop when it changes from outside (back/forward, a cleared
  // filter, a fresh URL), but ignore the prop update our own commit causes —
  // resyncing on that fights the draft the operator is still editing. A caller
  // that NORMALISES what we sent (trimming a UUID, dropping a value it rejected)
  // does reach this branch, and should: the field then shows what the URL
  // actually holds rather than a value the list is not filtered by.
  useEffect(() => {
    if (value === lastCommitted.current) return;

    lastCommitted.current = value;
    setDraft(value);
  }, [value]);

  function commit(next: string): void {
    if (next === lastCommitted.current) return;

    lastCommitted.current = next;
    onCommit(next);
  }

  return (
    <Input
      {...inputProps}
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') commit(draft);
      }}
      onBlur={() => commit(draft)}
    />
  );
}
